/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Application API endpoints.
 *
 * Two surfaces:
 *   - Public wizard (no auth, identified by wizard_token in URL)
 *   - Admin tracking (auth + IsControlUser)
 *
 * Tag types used:
 *   - "Application" - admin list/detail cache, invalidated on decisions
 *   - "WizardState" - the applicant's own wizard polling cache
 */
import { baseApi } from "@/app/store/api/baseApi";

import type {
  ApplicationAdminDetail,
  ApplicationAdminListRow,
  ClarifyResponse,
  WizardQuestion,
  WizardState,
  WizardStatus,
} from "../model/types";

// Add the new tags. The baseApi declares tagTypes statically but RTK Query
// is forgiving about extending it via the injectEndpoints call - we declare
// the tag identities here so providesTags / invalidatesTags type-check.
// We need to also extend baseApi's tagTypes list; that lives in baseApi.ts.

/** RTK Query endpoints for the pilot-application wizard. */
export const applicationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // -------------------------------------------------------------------------
    // Public wizard
    // -------------------------------------------------------------------------

    /** GET /api/v1/applications/wizard/questions/ - fixed question tree */
    getWizardQuestions: build.query<{ questions: WizardQuestion[] }, void>({
      query: () => "/applications/wizard/questions/",
    }),

    /** POST /api/v1/applications/wizard/start/ - mint token + start draft */
    startWizard: build.mutation<
      WizardState,
      { org_name: string; contact_email?: string }
    >({
      query: (body) => ({
        url: "/applications/wizard/start/",
        method: "POST",
        body,
      }),
      // Provides the WizardState cache for this token
      invalidatesTags: (_result, _error) =>
        _result ? [{ type: "WizardState", id: _result.wizard_token }] : [],
    }),

    /** GET /api/v1/applications/wizard/{token}/ - poll current state */
    getWizardState: build.query<WizardState, { token: string }>({
      query: ({ token }) => `/applications/wizard/${token}/`,
      providesTags: (_result, _error, { token }) => [
        { type: "WizardState", id: token },
      ],
    }),

    /** PATCH /api/v1/applications/wizard/{token}/answer/ - save one answer */
    saveWizardAnswer: build.mutation<
      WizardState,
      { token: string; key: string; value: unknown }
    >({
      query: ({ token, key, value }) => ({
        url: `/applications/wizard/${token}/answer/`,
        method: "PATCH",
        body: { key, value },
      }),
      // Re-fetch the state after a successful answer
      invalidatesTags: (_result, _error, { token }) => [
        { type: "WizardState", id: token },
      ],
    }),

    /** POST /wizard/{token}/clarify/ - have Claude generate next clarification */
    requestClarification: build.mutation<
      ClarifyResponse,
      { token: string; skip?: boolean }
    >({
      query: ({ token, skip }) => ({
        url: `/applications/wizard/${token}/clarify/`,
        method: "POST",
        body: skip ? { skip: true } : undefined,
      }),
      invalidatesTags: (_result, _error, { token }) => [
        { type: "WizardState", id: token },
      ],
    }),

    /** POST /wizard/{token}/clarify-answer/ - submit answer to last clarification */
    submitClarificationAnswer: build.mutation<
      WizardState,
      { token: string; answer: string }
    >({
      query: ({ token, answer }) => ({
        url: `/applications/wizard/${token}/clarify-answer/`,
        method: "POST",
        body: { answer },
      }),
      invalidatesTags: (_result, _error, { token }) => [
        { type: "WizardState", id: token },
      ],
    }),

    /** POST /wizard/{token}/synthesize/ - generate Claude synthesis */
    generateSynthesis: build.mutation<
      { synthesis: string; is_stub: boolean },
      { token: string }
    >({
      query: ({ token }) => ({
        url: `/applications/wizard/${token}/synthesize/`,
        method: "POST",
      }),
      // Synthesizing mutates the wizard's server-side state (the
      // generated synthesis is stored on the WizardState). Without
      // this, the cached getWizardState for the token stays stale and
      // the wizard UI keeps showing the pre-synthesis view until some
      // unrelated action invalidates it (audit RTK-V1).
      invalidatesTags: (_result, _error, { token }) => [
        { type: "WizardState", id: token },
      ],
    }),

    /** POST /wizard/{token}/submit/ - flip status to SUBMITTED */
    submitWizard: build.mutation<WizardState, { token: string }>({
      query: ({ token }) => ({
        url: `/applications/wizard/${token}/submit/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { token }) => [
        { type: "WizardState", id: token },
        // Admin caches should refresh too (a new submitted app appears)
        { type: "Application", id: "LIST" },
      ],
    }),

    // -------------------------------------------------------------------------
    // Admin / control plane
    // -------------------------------------------------------------------------

    /** GET /api/v1/control/applications/ - list, with optional filters */
    getApplicationsList: build.query<
      { results: ApplicationAdminListRow[]; count: number; next: string | null; previous: string | null },
      { status?: WizardStatus | string; page?: number; page_size?: number }
    >({
      query: ({ status, page, page_size }) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (page) params.set("page", String(page));
        if (page_size) params.set("page_size", String(page_size));
        const qs = params.toString();
        return `/control/applications/${qs ? `?${qs}` : ""}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((r) => ({ type: "Application" as const, id: r.id })),
              { type: "Application" as const, id: "LIST" },
            ]
          : [{ type: "Application" as const, id: "LIST" }],
    }),

    /** GET /api/v1/control/applications/{id}/ - detail */
    getApplicationDetail: build.query<ApplicationAdminDetail, { id: number }>({
      query: ({ id }) => `/control/applications/${id}/`,
      providesTags: (_result, _error, { id }) => [
        { type: "Application", id },
      ],
    }),

    /** POST /control/applications/{id}/decide/ - accept or decline */
    decideApplication: build.mutation<
      ApplicationAdminDetail,
      { id: number; status: "accepted" | "declined"; reason?: string }
    >({
      query: ({ id, status, reason }) => ({
        url: `/control/applications/${id}/decide/`,
        method: "POST",
        body: { status, reason: reason ?? "" },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Application", id },
        { type: "Application", id: "LIST" },
      ],
    }),

    /** POST /control/applications/{id}/provision/ - turn accepted app into a real Tenant */
    provisionApplication: build.mutation<
      {
        success: boolean;
        tenant_id: number;
        tenant_slug: string;
        owner_email: string;
        invite_url: string;
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/control/applications/${id}/provision/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Application", id },
        { type: "Application", id: "LIST" },
        // A new tenant appeared on the cohort list
        { type: "ControlTenant", id: "LIST" },
      ],
    }),

    /** POST /control/applications/{id}/resend-invite/ - regenerate + email */
    resendInvite: build.mutation<
      {
        success: boolean;
        tenant_id: number;
        tenant_slug: string;
        owner_email: string;
        invite_url: string;
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/control/applications/${id}/resend-invite/`,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWizardQuestionsQuery,
  useStartWizardMutation,
  useGetWizardStateQuery,
  useSaveWizardAnswerMutation,
  useRequestClarificationMutation,
  useSubmitClarificationAnswerMutation,
  useGenerateSynthesisMutation,
  useSubmitWizardMutation,
  useGetApplicationsListQuery,
  useGetApplicationDetailQuery,
  useDecideApplicationMutation,
  useProvisionApplicationMutation,
  useResendInviteMutation,
} = applicationApi;
